// src/pages/CreatorStorefront.jsx
// routechef.com/c/:handle  ·  the link a creator puts in their Instagram bio.
import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { getCreator, getCreatorTrips } from "../creator/creatorTripsData";
import { FEATURES } from "../config/features";
import { inr } from "../creator/creatorTripEngine";

const ORANGE = "#FF5B15";

export default function CreatorStorefront() {
    const { handle } = useParams();
    const creator = getCreator(handle);
    if (!FEATURES.ENABLE_CREATOR_TRIPS) return <Navigate to="/" replace />;
    if (!creator) return <Navigate to="/" replace />;
    const trips = getCreatorTrips(handle);

    return (
        <div style={S.page}>
            <style>{CSS}</style>
            <header style={S.top}>
                <Link to="/" style={{ textDecoration: "none", fontSize: 18 }}>
                    <span style={{ color: ORANGE, fontWeight: 700 }}>Route</span><span style={{ color: "#0A0A0A" }}>chef</span>
                </Link>
                <span style={S.pill}>Creator Trips</span>
            </header>

            <section style={S.hero}>
                <div style={S.avatar}>{creator.avatar_initials}</div>
                <h1 style={{ margin: "12px 0 2px", fontSize: 24 }}>{creator.name}</h1>
                <div style={{ color: "#64748B", fontSize: 14 }}>@{creator.handle} · {creator.followers} followers on {creator.platform}</div>
                <p style={{ maxWidth: 520, margin: "12px auto 0", color: "#334155", lineHeight: 1.55, fontSize: 15 }}>{creator.bio}</p>
                <div style={S.statsRow}>
                    <Stat n={creator.trips_booked.toLocaleString("en-IN")} l="trips booked" />
                    <Stat n={`★ ${creator.rating}`} l="traveller rating" />
                    <Stat n={trips.length} l="live trips" />
                </div>
            </section>

            <section style={S.grid}>
                {trips.map((t) => {
                    const fromPP = estimateFrom(t);
                    return (
                        <Link key={t.slug} to={`/c/${handle}/${t.slug}`} className="ct-card" style={S.card}>
                            <div style={{ ...S.cover, backgroundImage: `url(${t.cover})` }}>
                                <span style={S.views}>▶ {t.reel_views} views</span>
                            </div>
                            <div style={{ padding: "14px 16px 16px" }}>
                                <div style={{ fontWeight: 700, fontSize: 17, color: "#0F172A" }}>{t.title}</div>
                                <div style={{ fontSize: 13, color: "#64748B", marginTop: 4 }}>{t.subtitle}</div>
                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                                    {t.vibe.map((v) => <span key={v} style={S.tag}>{v}</span>)}
                                </div>
                                <div style={S.cardFoot}>
                                    <span style={{ fontSize: 12, color: "#64748B" }}>from <b style={{ color: "#0F172A", fontSize: 16 }}>{inr(fromPP)}</b> pp</span>
                                    <span style={S.cta}>Book from your city →</span>
                                </div>
                            </div>
                        </Link>
                    );
                })}
            </section>

            <footer style={{ textAlign: "center", color: "#94A3B8", fontSize: 12, padding: "24px 16px 40px" }}>
                Trips are designed by {creator.name}. Travel, stays and payments are handled by RouteChef.
            </footer>
        </div>
    );
}

// Rough "from" price on the card: inner trip + stays + markup, before getting there.
function estimateFrom(t) {
    let pp = t.creator_markup_pp;
    t.days.forEach((d) => d.blocks.forEach((b) => {
        if (b.type === "MOVE") pp += b.price_pp;
        if (b.type === "STAY") pp += (b.fallback_pn * b.nights) / 2;
    }));
    return Math.round(pp / 100) * 100;
}

const Stat = ({ n, l }) => (
    <div style={{ textAlign: "center", minWidth: 90 }}>
        <div style={{ fontWeight: 800, fontSize: 18, color: "#0F172A" }}>{n}</div>
        <div style={{ fontSize: 12, color: "#64748B" }}>{l}</div>
    </div>
);

const S = {
    page: { minHeight: "100vh", background: "#FAFAF9", fontFamily: "'Inter', -apple-system, system-ui, sans-serif" },
    top: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", background: "#fff", borderBottom: "1px solid #EEE" },
    pill: { fontSize: 11, fontWeight: 700, color: ORANGE, background: "#FFF1EA", padding: "4px 10px", borderRadius: 99, letterSpacing: 0.4 },
    hero: { textAlign: "center", padding: "36px 16px 20px" },
    avatar: { width: 84, height: 84, borderRadius: "50%", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 28, color: "#fff", background: `linear-gradient(135deg, ${ORANGE}, #F7931E)`, boxShadow: "0 0 0 4px #fff, 0 0 0 6px #FFD6C2" },
    statsRow: { display: "flex", justifyContent: "center", gap: 24, marginTop: 20, flexWrap: "wrap" },
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: 18, maxWidth: 1040, margin: "8px auto 0", padding: "0 16px" },
    card: { background: "#fff", borderRadius: 18, overflow: "hidden", border: "1px solid #EEE", textDecoration: "none", color: "inherit", display: "block" },
    cover: { height: 170, backgroundSize: "cover", backgroundPosition: "center", position: "relative" },
    views: { position: "absolute", left: 10, bottom: 10, background: "rgba(0,0,0,0.6)", color: "#fff", fontSize: 11, fontWeight: 600, padding: "4px 9px", borderRadius: 99 },
    tag: { fontSize: 11, color: "#475569", background: "#F1F5F9", padding: "3px 9px", borderRadius: 99 },
    cardFoot: { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14, paddingTop: 12, borderTop: "1px solid #F1F5F9" },
    cta: { fontSize: 12, fontWeight: 700, color: ORANGE },
};
const CSS = `.ct-card{transition:transform .15s, box-shadow .15s}.ct-card:hover{transform:translateY(-2px);box-shadow:0 10px 30px -12px rgba(15,23,42,.25)}`;
